import { Bug, Camera, Cpu, Droplets, FlaskConical, FlaskRound, HardDriveDownload, LayoutGrid, LogOut, MapPin, Radar, Settings2, Scan, Wifi } from "lucide-react";
import { AuthProvider, useAuth } from "context/AuthContext";
import NotificationBell from "components/notifications/NotificationBell";
import DashboardPage from "pages/Dashboard";
import CameraPage from "pages/Camera";
import FieldCameras from "pages/FieldCameras";
import NodeControl from "pages/NodeControl";
import NodePage from "pages/NodePage";
import RadarAnalysis from "pages/RadarAnalysis";
import RadarLab from "pages/RadarLab";
import ExperimentPage from "pages/Experiment";
import FirmwarePage from "pages/Firmware";
import ESP32DebugDashboard from "pages/Debug";
import DevicesOverviewPage from "pages/DevicesOverview";
import LoginPage from "pages/Login";
import WaterManagementPage from "pages/WaterManagement";
import {
  Link,
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
} from "react-router-dom";
import { DevBadge, DevRibbon } from "components/DevBadge";
import { IS_DEV_ENV } from "utils/env";
import padihatMark from "assets/padihat-mark.png";

// On dev builds a 24px hazard ribbon is pinned to the top; nudge the fixed
// chrome (sidebar, top bar) and content down so nothing hides behind it.
const devTop = IS_DEV_ENV ? "top-6" : "top-0";

const navItems = [
  { path: "/devices", label: "Devices", icon: LayoutGrid },
  { path: "/dashboard", label: "Detail", icon: Cpu },
  { path: "/camera", label: "Ruler Camera", icon: Camera },
  { path: "/node", label: "Node", icon: MapPin },
  { path: "/field-cameras", label: "Field Cameras", icon: Scan },
  { path: "/node-control", label: "Node Control", icon: Settings2 },
  { path: "/radar-analysis", label: "Radar Analysis", icon: Radar },
  { path: "/radar-lab", label: "Radar Lab", icon: FlaskRound },
  { path: "/experiment", label: "Experiment", icon: FlaskConical },
  { path: "/water",     label: "Water", icon: Droplets },
  { path: "/firmware",  label: "Firmware", icon: HardDriveDownload },
  { path: "/debug",     label: "Logs", icon: Bug },
];

const Navigation = () => {
  const location = useLocation();
  const isNavActive = (item) => {
    if (item.path === "/dashboard") {
      return location.pathname === "/dashboard" || /^\/devices\/[^/]+\/?$/.test(location.pathname);
    }
    if (item.path === "/camera") {
      return location.pathname === "/camera" || /^\/devices\/[^/]+\/camera\/?$/.test(location.pathname);
    }
    if (item.path === "/node") {
      return /^\/node(\/[^/]+)?\/?$/.test(location.pathname);
    }
    if (item.path === "/experiment") {
      return location.pathname === "/experiment" || /^\/devices\/[^/]+\/experiment\/?$/.test(location.pathname);
    }
    return location.pathname === item.path;
  };

  return (
    <>
      {/* ── Desktop sidebar ── */}
      <aside className={`hidden lg:flex flex-col w-64 fixed left-0 ${devTop} h-screen border-r border-slate-200/60 bg-slate-50 py-6 z-50`}>
        {/* Logo */}
        <div className="px-6 mb-8 flex items-center gap-3">
          <img src={padihatMark} alt="" className="w-10 h-10 object-contain" />
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-bold text-blue-900 font-headline leading-tight">
                PadiHAT
              </h1>
              <DevBadge />
            </div>
            <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
              Water Logger
            </p>
          </div>
        </div>

        {/* Nav links */}
        <nav className="flex-1 px-3 space-y-0.5">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = isNavActive(item);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 text-sm font-semibold tracking-tight transition-colors rounded-xl ${
                  isActive
                    ? "text-blue-700 bg-blue-50/60 border-r-[3px] border-blue-700 rounded-r-none font-bold"
                    : "text-slate-500 hover:text-blue-600 hover:bg-blue-50/50"
                }`}
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* System status widget */}
        <div className="px-6 mt-auto">
          <div className={`p-3 rounded-xl border ${IS_DEV_ENV ? "bg-amber-50 border-amber-200/80" : "bg-blue-50 border-blue-100/80"}`}>
            <p className={`text-[10px] font-bold uppercase tracking-wider ${IS_DEV_ENV ? "text-amber-700" : "text-blue-700"}`}>
              {IS_DEV_ENV ? "Environment: Development" : "Team workspace"}
            </p>
            <div className="flex items-center gap-2 mt-2">
              <div className={`w-2 h-2 rounded-full animate-pulse ${IS_DEV_ENV ? "bg-amber-500" : "bg-green-500"}`} />
              <span className="text-xs text-slate-600">
                {IS_DEV_ENV ? "Development workspace" : "Authorized access"}
              </span>
            </div>
          </div>
        </div>
      </aside>

      {/* ── Glass top bar ── */}
      <header className={`fixed ${devTop} z-40 w-full lg:ml-64 lg:w-[calc(100%-16rem)] h-16 flex items-center justify-between px-6 bg-white/70 backdrop-blur-xl border-b border-slate-200/50 shadow-sm`}>
        {/* Mobile: logo */}
        <div className="flex items-center gap-2 lg:hidden">
          <img src={padihatMark} alt="" className="w-8 h-8 object-contain" />
          <span className="text-base font-bold text-blue-900 font-headline">PadiHAT</span>
          <DevBadge />
        </div>
        {/* Desktop: brand name */}
        <span className="hidden lg:flex items-center gap-2.5 text-xl font-extrabold text-blue-800 font-headline tracking-tight">
          PadiHAT Water Logger
          <DevBadge />
        </span>

        {/* Online indicator + logout */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <span className="text-sm text-slate-500 hidden sm:inline">Signed in</span>
            <Wifi className="w-4 h-4 text-slate-400 ml-1" />
          </div>
          <NotificationBell />
          <LogoutButton />
        </div>
      </header>

      {/* ── Mobile bottom nav ── */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 shadow-[0_-4px_10px_rgba(0,0,0,0.05)] z-50 flex overflow-x-auto items-center h-16">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = isNavActive(item);
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex flex-col items-center justify-center flex-shrink-0 min-w-[76px] flex-1 py-1.5 gap-0.5 text-[10px] font-semibold transition-colors ${
                isActive
                  ? "text-blue-700"
                  : "text-slate-500"
              }`}
            >
              <div className={`p-1.5 rounded-xl transition-colors ${isActive ? "bg-blue-100" : ""}`}>
                <Icon className="w-4 h-4" />
              </div>
              <span>{item.label}</span>
            </Link>
          );
        })}
      </nav>
    </>
  );
};

const LogoutButton = () => {
  const { user, logout } = useAuth();
  if (!user) return null;
  return (
    <button
      onClick={logout}
      title="Sign out"
      className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-red-600 transition-colors px-2 py-1 rounded-lg hover:bg-red-50"
    >
      <LogOut className="w-3.5 h-3.5" />
      <span className="hidden sm:inline">Sign out</span>
    </button>
  );
};

const RequireAuth = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return null;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
};

const AppLayout = ({ children }) => (
  <div className="min-h-screen bg-surface font-body text-on-surface">
    <DevRibbon />
    <Navigation />
    {/* pt-16 = top bar height (+ dev ribbon when present); pb-20 lg:pb-0 = mobile bottom nav clearance */}
    <main className={`lg:ml-64 ${IS_DEV_ENV ? "pt-[5.5rem]" : "pt-16"} pb-20 lg:pb-6 min-h-screen`}>
      {children}
    </main>
  </div>
);

function App() {
  return (
    <Router>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="*"
            element={
              <RequireAuth>
                <AppLayout>
                  <Routes>
                    <Route path="/" element={<Navigate to="/devices" replace />} />
                    <Route path="/devices" element={<DevicesOverviewPage />} />
                    <Route path="/devices/:macAddress" element={<DashboardPage />} />
                    <Route path="/devices/:macAddress/camera" element={<CameraPage />} />
                    <Route path="/camera" element={<CameraPage />} />
                    <Route path="/field-cameras" element={<FieldCameras />} />
                    <Route path="/node" element={<NodePage />} />
                    <Route path="/node/:mac" element={<NodePage />} />
                    <Route path="/node-control" element={<NodeControl />} />
                    <Route path="/radar-analysis" element={<RadarAnalysis />} />
                    <Route path="/radar-lab" element={<RadarLab />} />
                    <Route path="/devices/:macAddress/experiment" element={<ExperimentPage />} />
                    <Route path="/experiment" element={<ExperimentPage />} />
                    <Route path="/dashboard" element={<DashboardPage />} />
                    {/* Trends was removed; old links land on Detail, which charts the same readings. */}
                    <Route path="/analytics" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/water" element={<WaterManagementPage />} />
                    <Route path="/firmware" element={<FirmwarePage />} />
                    {/* Old Settings page: device settings moved to Detail > Configuration, the rest is Firmware. */}
                    <Route path="/config" element={<Navigate to="/firmware" replace />} />
                    <Route path="/debug" element={<ESP32DebugDashboard />} />
                  </Routes>
                </AppLayout>
              </RequireAuth>
            }
          />
        </Routes>
      </AuthProvider>
    </Router>
  );
}

export default App;
